/**
 * Utility functions for managing onboarding progress in localStorage
 */

const ONBOARDING_STORAGE_KEY = 'company_onboarding_progress';

export interface OnboardingProgress {
  currentStepNumber: number;
  formValues: any;
  timestamp: string;
}

/**
 * Load saved onboarding progress from localStorage
 */
export const loadSavedProgress = (): OnboardingProgress | null => {
  try {
    const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    // console.error('Error loading saved onboarding progress:', error);
  }
  return null;
};

/**
 * Save onboarding progress to localStorage
 */
export const saveProgress = (formValues: any, stepNumber: number): void => {
  // Don't save if we're on the final step (onboarding complete)
  if (stepNumber === 6) {
    return;
  }

  const progressData: OnboardingProgress = {
    currentStepNumber: stepNumber,
    formValues: formValues,
    timestamp: new Date().toISOString(),
  };

  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(progressData));
  } catch (error) {
    // console.error('Error saving onboarding progress:', error);
  }
};

/**
 * Clear onboarding progress from localStorage
 */
export const clearProgress = (): void => {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch (error) {
    // console.error('Error clearing onboarding progress:', error);
  }
};

/**
 * Create a debounced version of the save function
 */
export const createDebouncedSave = (delay: number = 500) => {
  let timeoutRef: NodeJS.Timeout | null = null;

  const debouncedSave = (formValues: any, stepNumber: number) => {
    if (timeoutRef) {
      clearTimeout(timeoutRef);
    }

    timeoutRef = setTimeout(() => {
      saveProgress(formValues, stepNumber);
    }, delay);
  };

  const cleanup = () => {
    if (timeoutRef) {
      clearTimeout(timeoutRef);
    }
  };

  return { debouncedSave, cleanup };
};

